import { z } from "zod";

// The router's two peer doors (ADR-022 points 3 and 5, epic 26, S26.05/S26.06): the Go worker
// decides which facts to react to; notification decides who hears one and how a single delivery
// goes out. `planDeliveries` and `deliverOne` are reached only from `modules/worker-bridge`'s
// signed callback, never on the wire (the same shape `notify` already follows).

/** Every channel a delivery can go out on; `slack` is staff-only (ADR-022 point 6) and never a
 * person's own setting, unlike `NOTIFICATION_CHANNELS`. `transactionalEmail` is auth's own mail
 * (verification, password reset, both email-change emails, the address-taken notice, S26.12):
 * sent directly (`MailApi.send`), never held by a person's snooze, quiet hours or digest (ADR-022
 * point 9 narrows that to a notification a person's own settings govern; these are not one). */
export const DELIVERY_CHANNELS = ["email", "slack", "inApp", "push", "transactionalEmail"] as const;
export const deliveryChannelSchema = z.enum(DELIVERY_CHANNELS);
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

/** A fact as the router hands it to notification: enough to decide who hears it and to render
 * the delivery later, never a secret (ADR-022 point 7). */
export const routerFactInputSchema = z.object({
  eventId: z.string().min(1),
  tenantId: z.string().min(1),
  aggregateType: z.string().min(1),
  type: z.string().min(1),
  data: z.unknown(),
  occurredAt: z.string().min(1),
});
export type RouterFactInput = z.infer<typeof routerFactInputSchema>;

/** One delivery `planDeliveries` decided on; `deliveryKey` is `<eventId>:<channel>:<recipient>`,
 * so a redelivered fact plans the same keys again (ADR-022 point 3). */
export const plannedDeliverySchema = z.object({
  channel: deliveryChannelSchema,
  recipient: z.string().min(1),
  kind: z.string().min(1),
  deliveryKey: z.string().min(1),
});
export type PlannedDelivery = z.infer<typeof plannedDeliverySchema>;

export const plannedDeliveriesSchema = z.array(plannedDeliverySchema);

/** One delivery `deliverOne` is asked to send; it carries the originating fact so notification
 * never has to look the event log back up to render it. A `transactionalEmail` delivery whose
 * `kind` needs a link (auth's own three, an invitation) mints it itself now, through its own
 * `auth`/`organization` peers (ADR-022 revision 5): the peer cycle that once forced worker-bridge
 * to mint on notification's behalf is gone (organization peers only account since D513). */
export const deliverInputSchema = z.object({
  deliveryKey: z.string().min(1),
  eventId: z.string().min(1),
  tenantId: z.string().min(1),
  channel: deliveryChannelSchema,
  recipient: z.string().min(1),
  kind: z.string().min(1),
  factType: z.string().min(1),
  factData: z.unknown(),
});
export type DeliverInput = z.infer<typeof deliverInputSchema>;

export const DELIVER_OUTCOMES = ["delivered", "already-delivered"] as const;
export const deliverOutputSchema = z.object({ outcome: z.enum(DELIVER_OUTCOMES) });
export type DeliverOutput = z.infer<typeof deliverOutputSchema>;

/** The third peer door (ADR-022 point 9 revision 4, S26.16): a wake of
 * `notification-hold.email_held` (notification-hold.events.ts), reached the same way as `plan`
 * and `deliver`, a pure timer on the Go side. Notification alone decides what is due and re-
 * appends the fact when something remains. */
export const releaseHoldInputSchema = z.object({ userId: z.string().min(1) });
export type ReleaseHoldInput = z.infer<typeof releaseHoldInputSchema>;

/** The fourth peer door (ADR-026 point 9, epic 30, S30.13, D810): the `erasure` process
 * manager's own day-before reminder wake, a pure timer carrying no fact of its own. Carries the
 * still-live email and language straight off the state `account.erasure_requested` already gave
 * it, the same reasoning `account.erasure_initiated` itself takes for the "erasure done" email —
 * but `sendErasureReminder` itself re-checks the account's own live `erasureProgress` before
 * sending (review group 2, decision e): a wake fired before a cancellation or a staff-driven,
 * no-grace erasure landed must never mail a reminder for a deletion that is no longer pending
 * and un-initiated. `purgeAt`, the request's own stable value, keys the delivery's own dedupe
 * (never `personId` alone), so a cancelled-then-re-requested deletion's own reminder is never
 * silently swallowed by the first request's already-delivered row. */
export const sendErasureReminderInputSchema = z.object({
  personId: z.string().min(1),
  email: z.string().min(1),
  language: z.string().min(1),
  purgeAt: z.string().min(1),
});
export type SendErasureReminderInput = z.infer<typeof sendErasureReminderInputSchema>;
